import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API_URL } from '../components/config.js';
import './PublicProjectView.css';

const PublicProjectView = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects/${id}/public`);
      setProject(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Project not found or not available for public viewing');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      setError('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      await axios.post(`${API_URL}/api/projects/${id}/comments`, {
        content: comment.trim()
      });

      setComment('');
      setSubmitSuccess(true);
      
      // Refresh project to get updated comment count
      setTimeout(() => {
        fetchProject();
        setSubmitSuccess(false);
      }, 2000);

    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-active',
      completed: 'status-completed'
    };
    
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="public-project-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="public-project-view">
        <div className="error-container">
          <h2>Project Not Available</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-project-view">
      <div className="project-container">
        <div className="project-header">
          <div className="project-title-section">
            <h1>{project.name}</h1>
            <div className="project-status">
              {getStatusBadge(project.status)}
              <span className="comment-count">{project.commentCount} comments</span>
            </div>
          </div>
          <div className="project-meta">
            <span>Published: {formatDate(project.createdAt)}</span>
          </div>
        </div>

        {project.imageUrl && (
          <div className="project-image">
            <img src={`${API_URL}${project.imageUrl}`} alt={project.name} />
          </div>
        )}

        <div className="project-content">
          <div className="project-description">
            <h2>About This Project</h2>
            <div className="description-text">
              {project.description.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="comment-section">
            <h2>Community Feedback</h2>
            <p className="comment-intro">
              We value your input on this project. Your feedback helps us make informed decisions 
              and better serve our community. All comments are anonymous.
            </p>

            {/* Comment Form */}
            <div className="comment-form-container">
              <h3>Share Your Thoughts</h3>
              {submitSuccess && (
                <div className="success-message">
                  Thank you for your feedback! Your comment has been submitted successfully.
                </div>
              )}
              
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handleCommentSubmit} className="comment-form">
                <div className="form-group">
                  <label htmlFor="comment">Your Comment</label>
                  <textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Please share your thoughts, concerns, or suggestions about this project..."
                    rows="5"
                    maxLength="1000"
                    disabled={submitting}
                  />
                  <div className="character-count">
                    {comment.length}/1000 characters
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={submitting || !comment.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit Anonymous Feedback'}
                </button>
              </form>

              <div className="privacy-notice">
                <h4>🔒 Privacy Notice</h4>
                <ul>
                  <li>Your comments are submitted anonymously</li>
                  <li>No personal information is collected or stored</li>
                  <li>Comments are reviewed by staff for moderation purposes</li>
                  <li>Please be respectful and constructive in your feedback</li>
                </ul>
              </div>
            </div>

            {/* Community Feedback Stats */}
            <div className="feedback-stats">
              <div className="stats-card">
                <div className="stats-number">{project.commentCount}</div>
                <div className="stats-label">Community Comments</div>
              </div>
              <div className="stats-card">
                <div className="stats-number">{project.status === 'active' ? 'Open' : 'Closed'}</div>
                <div className="stats-label">Feedback Status</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProjectView;